<!-- User Management Dashboard -->
<div class="h-full flex flex-col gap-4">
  <!-- Header Section -->
  <div
    class="bg-white rounded-lg shadow dark:bg-gray-800 p-4 md:p-6 flex-shrink-0"
  >
    <div class="flex justify-between items-center">
      <div>
        <h6
          class="leading-none text-2xl font-bold text-gray-900 dark:text-white pb-2"
        >
          {{ activeTab === "users" ? "Registered Users" : "User Invitations" }}
        </h6>
        <p class="mt-1 text-sm font-medium text-gray-500 dark:text-gray-400">
          {{
            activeTab === "users" ?
              "Manage existing user accounts"
            : "Send invitations to new users"
          }}
        </p>
      </div>

      <div class="flex gap-4 items-center">
        <!-- District Filter (Admin only) -->
        @if (isAdmin) {
          <div class="flex items-center gap-2">
            <div
              class="flex items-center gap-1.5 text-sm font-medium text-gray-500 dark:text-white whitespace-nowrap"
            >
              <svg
                class="w-4 h-4 text-blue-500"
                fill="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  d="M11.906 1.994a8.002 8.002 0 0 1 8.09 8.421 7.996 7.996 0 0 1-1.297 3.957.996.996 0 0 1-.133.204l-.108.129c-.178.243-.37.477-.573.699l-5.112 6.224a1 1 0 0 1-1.545 0L5.982 15.26l-.002-.002a18.146 18.146 0 0 1-.309-.38l-.133-.163a.999.999 0 0 1-.13-.202 7.995 7.995 0 0 1 6.498-12.518ZM15 9.997a3 3 0 1 1-5.999 0 3 3 0 0 1 5.999 0Z"
                  clip-rule="evenodd"
                />
              </svg>
              Filter by District
            </div>
            <select
              [(ngModel)]="selectedDistrictId"
              (ngModelChange)="onDistrictChange()"
              class="block p-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            >
              <option [ngValue]="null">All Districts</option>
              @for (client of clients; track client.id) {
                <option [ngValue]="client.id">{{ client.name }}</option>
              }
            </select>
          </div>
        }

        <!-- Search Input -->
        <div class="relative w-72">
          <div
            class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none"
          >
            <svg
              class="w-4 h-4 text-gray-500 dark:text-gray-400"
              fill="none"
              viewBox="0 0 20 20"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
              />
            </svg>
          </div>
          @if (activeTab === "users") {
            <input
              type="search"
              [(ngModel)]="searchTerm"
              (ngModelChange)="onSearchChange()"
              placeholder="Search users..."
              class="block w-full p-2 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-white dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            />
          } @else {
            <input
              type="search"
              [(ngModel)]="invitationSearchTerm"
              (ngModelChange)="onInvitationSearchChange()"
              placeholder="Search invitations..."
              class="block w-full p-2 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-white dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            />
          }
        </div>

        <!-- Action Buttons (Invitations tab only) -->
        @if (activeTab === "invitations") {
          <div class="flex gap-2">
            <button
              (click)="openImportInvitationsModal()"
              type="button"
              class="uppercase text-sm font-semibold inline-flex items-center rounded-lg bg-green-600 text-white hover:bg-green-700 focus:ring-4 focus:ring-green-300 dark:bg-green-600 dark:hover:bg-green-700 dark:focus:ring-green-800 px-4 py-2 transition-all duration-200"
            >
              <svg
                class="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 5v9m-5 0H5a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-4a1 1 0 0 0-1-1h-2M8 9l4-5 4 5m0 8h.01"
                />
              </svg>
              IMPORT CSV
            </button>
            <button
              (click)="openSendInvitationModal()"
              type="button"
              class="uppercase text-sm font-semibold inline-flex items-center rounded-lg bg-blue-600 text-white hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800 px-4 py-2 transition-all duration-200"
            >
              <svg
                class="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              SEND INVITATION
            </button>
          </div>
        }

        <!-- Download Filtered List button -->
        <button
          title="Download the filtered list as CSV"
          (click)="downloadFilteredList()"
          [disabled]="
            activeTab === 'users' ?
              filteredUsers.length === 0
            : filteredUserInvitations.length === 0
          "
          class="uppercase text-xs 3xl:text-sm font-semibold inline-flex gap-1 items-center rounded-lg bg-blue-600 text-white hover:bg-blue-700 dark:focus:ring-gray-700 px-4 py-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <svg
            class="w-6 h-6"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 15v2a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3v-2m-8 1V4m0 12-4-4m4 4 4-4"
            />
          </svg>
          Download
        </button>

        <!-- Tabs Navigation -->
        <div class="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
          <button
            (click)="setActiveTab('users')"
            [class]="
              activeTab === 'users' ?
                'bg-white dark:bg-gray-600 text-blue-600 dark:text-white shadow-sm'
              : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            "
            class="px-4 py-2 rounded-md font-medium text-sm flex items-center transition-all duration-200 ml-1"
          >
            <svg
              class="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
              />
            </svg>
            Users
            <span
              class="ml-2 bg-green-100 text-green-800 text-xs font-medium px-2 py-0.5 rounded-full dark:bg-green-900 dark:text-green-300"
            >
              {{ filteredUsers.length }}
            </span>
          </button>
          <button
            (click)="setActiveTab('invitations')"
            [class]="
              activeTab === 'invitations' ?
                'bg-white dark:bg-gray-600 text-blue-600 dark:text-white shadow-sm'
              : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white'
            "
            class="px-4 py-2 rounded-md font-medium text-sm flex items-center transition-all duration-200 ml-1"
          >
            <svg
              class="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            Invitations
            <span
              class="ml-2 bg-green-100 text-green-800 text-xs font-medium px-2 py-0.5 rounded-full dark:bg-green-900 dark:text-green-300"
            >
              {{ filteredUserInvitations.length }}
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- User Invitations Tab Content -->
  <div
    *ngIf="activeTab === 'invitations'"
    class="flex flex-col gap-4 flex-1 min-h-0"
  >
    <!-- User Invitations Table -->
    <div
      class="bg-white rounded-lg shadow dark:bg-gray-800 flex-1 flex flex-col overflow-hidden"
    >
      <!-- Loading State -->
      <div
        *ngIf="isLoadingInvitations"
        class="flex-1 flex items-center justify-center"
      >
        <div class="flex items-center space-x-2">
          <svg
            class="animate-spin h-5 w-5 text-blue-600"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <span class="text-gray-600 dark:text-gray-300"
            >Loading invitations...</span
          >
        </div>
      </div>

      <!-- Table -->
      <div *ngIf="!isLoadingInvitations" class="flex-1 overflow-auto p-1">
        <table
          class="w-full text-sm text-left text-gray-500 dark:text-gray-400"
        >
          <thead
            class="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400 sticky top-0"
          >
            <tr>
              <th
                scope="col"
                class="px-6 py-3 cursor-pointer"
                (click)="onInvitationHeaderSort('Name')"
              >
                <div class="flex items-center space-x-2">
                  <span>Name</span>
                  <span class="text-sm leading-none">
                    <ng-container [ngSwitch]="getInvitationSortIcon('Name')">
                      <span *ngSwitchCase="'up'">▲</span>
                      <span *ngSwitchCase="'down'">▼</span>
                      <span *ngSwitchDefault>↕</span>
                    </ng-container>
                  </span>
                </div>
              </th>
              <th
                scope="col"
                class="px-6 py-3 cursor-pointer"
                (click)="onInvitationHeaderSort('Email')"
              >
                <div class="flex items-center space-x-2">
                  <span>Email</span>
                  <span class="text-sm leading-none">
                    <ng-container [ngSwitch]="getInvitationSortIcon('Email')">
                      <span *ngSwitchCase="'up'">▲</span>
                      <span *ngSwitchCase="'down'">▼</span>
                      <span *ngSwitchDefault>↕</span>
                    </ng-container>
                  </span>
                </div>
              </th>
              <th *ngIf="isAdmin" scope="col" class="px-6 py-3">Role</th>
              <th
                scope="col"
                class="px-6 py-3 cursor-pointer"
                (click)="onInvitationHeaderSort('Created Date')"
              >
                <div class="flex items-center space-x-2">
                  <span>Sent Date</span>
                  <span class="text-sm leading-none">
                    <ng-container
                      [ngSwitch]="getInvitationSortIcon('Created Date')"
                    >
                      <span *ngSwitchCase="'up'">▲</span>
                      <span *ngSwitchCase="'down'">▼</span>
                      <span *ngSwitchDefault>↕</span>
                    </ng-container>
                  </span>
                </div>
              </th>
              <th
                scope="col"
                class="px-6 py-3 cursor-pointer"
                (click)="onInvitationHeaderSort('Expires')"
              >
                <div class="flex items-center space-x-2">
                  <span>Expires</span>
                  <span class="text-sm leading-none">
                    <ng-container [ngSwitch]="getInvitationSortIcon('Expires')">
                      <span *ngSwitchCase="'up'">▲</span>
                      <span *ngSwitchCase="'down'">▼</span>
                      <span *ngSwitchDefault>↕</span>
                    </ng-container>
                  </span>
                </div>
              </th>
              <th scope="col" class="px-6 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              *ngFor="
                let invitation of paginatedInvitations;
                trackBy: trackByInvitationId
              "
              class="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              <!-- Name Column -->
              <th
                scope="row"
                class="px-6 py-4 text-gray-900 whitespace-nowrap dark:text-white"
              >
                <div class="text-base font-semibold">{{ invitation.name }}</div>
              </th>

              <!-- Email Column -->
              <td class="px-6 py-4">{{ invitation.email }}</td>

              <!-- Role Column (Admin only) -->
              <td *ngIf="isAdmin" class="px-6 py-4">
                <span
                  class="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold"
                  [ngClass]="{
                    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400':
                      invitation.role === 'GROWER',
                    'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400':
                      invitation.role === 'MANAGER',
                    'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400':
                      invitation.role === 'ENTITY',
                    'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400':
                      invitation.role === 'ADMIN',
                    'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400':
                      !invitation.role,
                  }"
                >
                  {{
                    invitation.role ? (invitation.role | titlecase) : "Unknown"
                  }}
                </span>
              </td>

              <!-- Sent Date Column -->
              <td class="px-6 py-4">
                {{ formatDateTime(invitation.created_at) }}
              </td>

              <!-- Expires Column -->
              <td class="px-6 py-4">
                {{ formatDateTime(invitation.expires_at) }}
              </td>

              <!-- Actions Column -->
              <td class="px-6 py-4">
                <div class="flex space-x-2">
                  <button
                    (click)="resendInvitation(invitation)"
                    class="inline-flex items-center px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 rounded-md hover:bg-green-100 focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 transition-all duration-200 dark:text-green-400 dark:bg-green-900/20 dark:hover:bg-green-900/30 dark:focus:ring-green-400"
                  >
                    <svg
                      class="w-3 h-3 mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    Resend
                  </button>
                  <button
                    (click)="openDeleteInvitationModal(invitation)"
                    class="inline-flex items-center px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-md hover:bg-red-100 focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 transition-all duration-200 dark:text-red-400 dark:bg-red-900/20 dark:hover:bg-red-900/30 dark:focus:ring-red-400"
                  >
                    <svg
                      class="w-3 h-3 mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Empty State -->
        <div
          *ngIf="filteredUserInvitations.length === 0"
          class="text-center py-8"
        >
          <svg
            class="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
          <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">
            No invitations found
          </h3>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {{
              invitationSearchTerm ?
                "Try adjusting your search text."
              : "Get started by sending your first invitation."
            }}
          </p>
        </div>
      </div>

      <!-- Pagination Controls -->
      <div
        *ngIf="!isLoadingInvitations && paginatedInvitations.length > 0"
        class="flex items-center justify-between px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex-shrink-0"
      >
        <!-- Items per page -->
        <div class="flex items-center space-x-2">
          <span class="text-sm text-gray-700 dark:text-gray-300">Show:</span>
          <select
            [(ngModel)]="invitationItemsPerPage"
            (ngModelChange)="changeInvitationItemsPerPage()"
            class="text-sm border border-gray-300 rounded px-2 py-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            <option
              *ngFor="let option of itemsPerPageOptions"
              [ngValue]="option"
            >
              {{ option }}
            </option>
          </select>
          <span class="text-sm text-gray-700 dark:text-gray-300">per page</span>
        </div>

        <!-- Page info -->
        <div class="text-sm text-gray-700 dark:text-gray-300">
          Showing
          {{ (invitationCurrentPage - 1) * invitationItemsPerPage + 1 }} to
          {{
            Math.min(
              invitationCurrentPage * invitationItemsPerPage,
              invitationTotalItems
            )
          }}
          of {{ invitationTotalItems }} invitations
        </div>

        <!-- Pagination buttons -->
        <div class="flex items-center space-x-1">
          <button
            (click)="goToInvitationPage(invitationCurrentPage - 1)"
            [disabled]="invitationCurrentPage === 1"
            class="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed dark:border-gray-600 dark:hover:bg-gray-700 dark:text-white"
          >
            Previous
          </button>

          <button
            *ngFor="let page of getVisibleInvitationPages()"
            (click)="page !== -1 && goToInvitationPage(page)"
            [class]="
              page === invitationCurrentPage ?
                'bg-blue-500 text-white'
              : 'bg-white text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600'
            "
            class="px-3 py-1 text-sm border border-gray-300 rounded-md dark:border-gray-600"
            [disabled]="page === -1"
          >
            {{ page === -1 ? "..." : page }}
          </button>

          <button
            (click)="goToInvitationPage(invitationCurrentPage + 1)"
            [disabled]="invitationCurrentPage === invitationTotalPages"
            class="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed dark:border-gray-600 dark:hover:bg-gray-700 dark:text-white"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- User Management Tab Content -->
  <div *ngIf="activeTab === 'users'" class="flex flex-col gap-4 flex-1 min-h-0">
    <!-- User Management Table -->
    <div
      class="bg-white rounded-lg shadow dark:bg-gray-800 flex flex-col flex-1 min-h-0"
    >
      <!-- Loading State -->
      <div *ngIf="isLoading" class="flex-1 flex items-center justify-center">
        <div class="flex items-center space-x-2">
          <svg
            class="animate-spin h-5 w-5 text-blue-600"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <span class="text-gray-600 dark:text-gray-300">Loading users...</span>
        </div>
      </div>

      <!-- Table -->
      <div *ngIf="!isLoading" class="flex-1 overflow-auto p-1">
        <table
          class="w-full text-sm text-left text-gray-500 dark:text-gray-400"
        >
          <thead
            class="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400 sticky top-0"
          >
            <tr>
              <th
                scope="col"
                class="px-6 py-3 cursor-pointer"
                (click)="onUserHeaderSort('First Name')"
              >
                <div class="flex items-center space-x-2">
                  <span>First Name</span>
                  <span class="text-sm leading-none">
                    <ng-container [ngSwitch]="getUserSortIcon('First Name')">
                      <span *ngSwitchCase="'up'">▲</span>
                      <span *ngSwitchCase="'down'">▼</span>
                      <span *ngSwitchDefault>↕</span>
                    </ng-container>
                  </span>
                </div>
              </th>
              <th
                scope="col"
                class="px-6 py-3 cursor-pointer"
                (click)="onUserHeaderSort('Last Name')"
              >
                <div class="flex items-center space-x-2">
                  <span>Last Name</span>
                  <span class="text-sm leading-none">
                    <ng-container [ngSwitch]="getUserSortIcon('Last Name')">
                      <span *ngSwitchCase="'up'">▲</span>
                      <span *ngSwitchCase="'down'">▼</span>
                      <span *ngSwitchDefault>↕</span>
                    </ng-container>
                  </span>
                </div>
              </th>
              <th
                scope="col"
                class="px-6 py-3 cursor-pointer"
                (click)="onUserHeaderSort('Email')"
              >
                <div class="flex items-center space-x-2">
                  <span>Email</span>
                  <span class="text-sm leading-none">
                    <ng-container [ngSwitch]="getUserSortIcon('Email')">
                      <span *ngSwitchCase="'up'">▲</span>
                      <span *ngSwitchCase="'down'">▼</span>
                      <span *ngSwitchDefault>↕</span>
                    </ng-container>
                  </span>
                </div>
              </th>
              <th *ngIf="isAdmin" scope="col" class="px-6 py-3">Role</th>
              <th
                scope="col"
                class="px-6 py-3 cursor-pointer"
                (click)="onUserHeaderSort('Last Login')"
              >
                <div class="flex items-center space-x-2">
                  <span>Last Login</span>
                  <span class="text-sm leading-none">
                    <ng-container [ngSwitch]="getUserSortIcon('Last Login')">
                      <span *ngSwitchCase="'up'">▲</span>
                      <span *ngSwitchCase="'down'">▼</span>
                      <span *ngSwitchDefault>↕</span>
                    </ng-container>
                  </span>
                </div>
              </th>
              <th
                scope="col"
                class="px-6 py-3 cursor-pointer"
                (click)="onUserHeaderSort('Created Date')"
              >
                <div class="flex items-center space-x-2">
                  <span>Created</span>
                  <span class="text-sm leading-none">
                    <ng-container [ngSwitch]="getUserSortIcon('Created Date')">
                      <span *ngSwitchCase="'up'">▲</span>
                      <span *ngSwitchCase="'down'">▼</span>
                      <span *ngSwitchDefault>↕</span>
                    </ng-container>
                  </span>
                </div>
              </th>
              <th *ngIf="isAdmin" scope="col" class="px-6 py-3">
                Allowed Districts
              </th>
              <th scope="col" class="px-6 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              *ngFor="let user of paginatedUsers; trackBy: trackByUserId"
              class="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              <!-- First Name -->
              <th
                scope="row"
                class="px-6 py-4 text-gray-900 whitespace-nowrap dark:text-white"
              >
                <div class="text-base font-semibold">
                  {{ user.first_name }}
                </div>
              </th>

              <!-- Last Name -->
              <td class="px-6 py-4">{{ user.last_name }}</td>

              <!-- Email Column -->
              <td class="px-6 py-4">{{ user.email }}</td>

              <!-- Role Column (Admin only) -->
              <td *ngIf="isAdmin" class="px-6 py-4">
                <span
                  class="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold"
                  [ngClass]="{
                    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400':
                      user.role === 'GROWER',
                    'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400':
                      user.role === 'MANAGER',
                    'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400':
                      user.role === 'ENTITY',
                  }"
                >
                  {{ user.role | titlecase }}
                </span>
              </td>

              <!-- Last Login Column -->
              <td class="px-6 py-4">
                {{ formatDateTime(user.latestloginat) }}
              </td>

              <!-- Created Date Column -->
              <td class="px-6 py-4">{{ formatDateTime(user.createdat) }}</td>

              <!-- Allowed Districts Column (Admin only) -->
              <td *ngIf="isAdmin" class="px-6 py-4">
                <div class="flex flex-wrap gap-1">
                  <span
                    *ngFor="
                      let name of getDistrictNames(user.allowed_districts)
                    "
                    class="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                  >
                    {{ name }}
                  </span>
                  <span
                    *ngIf="!user.allowed_districts?.length"
                    class="text-gray-400"
                    >N/A</span
                  >
                </div>
              </td>

              <!-- Actions Column -->
              <td class="px-6 py-4">
                <div class="flex space-x-2">
                  <button
                    (click)="openEditModal(user)"
                    class="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-all duration-200 dark:text-blue-400 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 dark:focus:ring-blue-400"
                  >
                    <svg
                      class="w-3 h-3 mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    Edit
                  </button>
                  <!-- Delete disabled for now
                  <button
                    (click)="openDeleteModal(user)"
                    class="inline-flex items-center px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-md hover:bg-red-100 focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 transition-all duration-200 dark:text-red-400 dark:bg-red-900/20 dark:hover:bg-red-900/30 dark:focus:ring-red-400"
                  >
                    <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                    Delete
                  </button>
                  -->
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Empty State -->
        <div *ngIf="filteredUsers.length === 0" class="text-center py-8">
          <svg
            class="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
            />
          </svg>
          <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">
            No users found
          </h3>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {{
              searchTerm ?
                "Try adjusting your search criteria."
              : "No registered users available."
            }}
          </p>
        </div>
      </div>

      <!-- Pagination Controls -->
      <div
        *ngIf="paginatedUsers.length > 0"
        class="flex items-center justify-between px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"
      >
        <!-- Items per page -->
        <div class="flex items-center space-x-2">
          <span class="text-sm text-gray-700 dark:text-gray-300">Show:</span>
          <select
            [(ngModel)]="itemsPerPage"
            (ngModelChange)="changeItemsPerPage()"
            class="text-sm border border-gray-300 rounded px-2 py-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            <option
              *ngFor="let option of itemsPerPageOptions"
              [ngValue]="option"
            >
              {{ option }}
            </option>
          </select>
          <span class="text-sm text-gray-700 dark:text-gray-300">per page</span>
        </div>

        <!-- Page info -->
        <div class="text-sm text-gray-700 dark:text-gray-300">
          Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to
          {{ Math.min(currentPage * itemsPerPage, totalItems) }} of
          {{ totalItems }} users
        </div>

        <!-- Pagination buttons -->
        <div class="flex items-center space-x-1">
          <button
            (click)="goToPage(currentPage - 1)"
            [disabled]="currentPage === 1"
            class="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed dark:border-gray-600 dark:hover:bg-gray-700 dark:text-white"
          >
            Previous
          </button>

          <button
            *ngFor="let page of getVisiblePages()"
            (click)="page !== -1 && goToPage(page)"
            [class]="
              page === currentPage ?
                'bg-blue-500 text-white'
              : 'bg-white text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600'
            "
            class="px-3 py-1 text-sm border border-gray-300 rounded-md dark:border-gray-600"
            [disabled]="page === -1"
          >
            {{ page === -1 ? "..." : page }}
          </button>

          <button
            (click)="goToPage(currentPage + 1)"
            [disabled]="currentPage === totalPages"
            class="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed dark:border-gray-600 dark:hover:bg-gray-700 dark:text-white"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- CSV Import Modal for Invitations -->
  <div
    *ngIf="isImportInvitationsModalOpen"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
  >
    <div
      class="bg-white rounded-lg shadow dark:bg-gray-700 w-full max-w-2xl mx-4"
    >
      <div
        class="flex items-center justify-between p-4 md:p-5 border-b rounded-t dark:border-gray-600"
      >
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
          Import Invitations from CSV
        </h3>
        <button
          (click)="closeModals()"
          type="button"
          class="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white"
        >
          <svg class="w-3 h-3" fill="none" viewBox="0 0 14 14">
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
            />
          </svg>
        </button>
      </div>

      <div
        class="p-4 md:p-5 space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto"
      >
        <!-- Download Template -->
        <div
          class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4"
        >
          <div class="flex items-start space-x-3">
            <svg
              class="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div class="flex-1">
              <h4 class="text-sm font-medium text-blue-900 dark:text-blue-100">
                Download CSV Template
              </h4>
              <p class="text-sm text-blue-700 dark:text-blue-300 mt-1">
                Use our template to ensure your CSV file has the correct format
                and required columns (name, email).
              </p>
              <button
                (click)="downloadTemplate()"
                class="mt-2 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
              >
                Download Template →
              </button>
            </div>
          </div>
        </div>

        <!-- File Upload -->
        <div>
          <label
            class="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
            >Select CSV File</label
          >
          <input
            type="file"
            (change)="onFileSelect($event)"
            accept=".csv,.xlsx"
            class="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 dark:text-gray-400 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400"
          />
          <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
            CSV or Excel files only. Max file size: 10MB
          </p>
        </div>

        <!-- User Type Dropdown for CSV Import - Only visible for internal users -->
        <div *ngIf="isInternalUser">
          <label
            class="block mb-2 text-sm font-medium text-gray-900 dark:text-white flex items-center"
          >
            <svg
              class="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
            User Type *
          </label>
          <select
            [(ngModel)]="csvImportUserType"
            name="csvImportUserType"
            class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400 dark:text-white"
          >
            <option value="GROWER">Grower</option>
            <option value="MANAGER">Manager</option>
          </select>
          <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
            All users in the CSV will be invited as this type
          </p>
        </div>

        <!-- Client Dropdown for CSV Import - Only visible for internal users -->
        <div *ngIf="isInternalUser">
          <label
            class="block mb-2 text-sm font-medium text-gray-900 dark:text-white flex items-center"
          >
            <svg
              class="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
            Client *
          </label>
          <select
            [(ngModel)]="csvImportClientId"
            name="csvImportClientId"
            class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400 dark:text-white"
          >
            <option [ngValue]="null" disabled>Select a client</option>
            <option *ngFor="let client of clients" [ngValue]="client.id">
              {{ client.name }}
            </option>
          </select>
          <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
            All users in the CSV will be associated with this client
          </p>
        </div>

        <!-- Process File Button -->
        <button
          *ngIf="csvFile && csvErrors.length === 0"
          (click)="processCsvFile()"
          [disabled]="isProcessingCsv"
          class="w-full text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800 disabled:opacity-50"
        >
          <span *ngIf="!isProcessingCsv">Process File</span>
          <span
            *ngIf="isProcessingCsv"
            class="flex items-center justify-center"
          >
            <svg
              class="animate-spin h-4 w-4 mr-2"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Processing...
          </span>
        </button>

        <!-- Errors Display -->
        <div
          *ngIf="csvErrors.length > 0"
          class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4"
        >
          <h4 class="text-sm font-medium text-red-900 dark:text-red-100 mb-2">
            Validation Errors ({{ csvErrors.length }})
          </h4>
          <ul
            class="text-sm text-red-700 dark:text-red-300 space-y-1 max-h-48 overflow-y-auto"
          >
            <li
              *ngFor="let error of csvErrors"
              class="flex items-start space-x-2"
            >
              <span class="text-red-500 mt-0.5">•</span>
              <span class="break-all">{{ error }}</span>
            </li>
          </ul>
        </div>

        <!-- Preview Data -->
        <div
          *ngIf="csvData.length > 0 && csvErrors.length === 0"
          class="space-y-3"
        >
          <div class="flex items-center justify-between">
            <h4
              class="text-sm font-medium text-gray-900 dark:text-white flex items-center"
            >
              <svg
                class="w-4 h-4 mr-2 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Preview Data ({{ csvData.length }} invitations ready to send)
            </h4>
            <span
              class="text-xs px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 rounded-full"
            >
              ✓ Validation Passed
            </span>
          </div>

          <div
            class="max-h-60 overflow-auto border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800"
          >
            <table class="w-full text-xs">
              <thead class="bg-gray-100 dark:bg-gray-700 sticky top-0">
                <tr>
                  <th
                    class="px-2 py-2 text-left font-medium text-gray-700 dark:text-gray-300 text-xs"
                  >
                    #
                  </th>
                  <th
                    class="px-2 py-2 text-left font-medium text-gray-700 dark:text-gray-300 text-xs"
                  >
                    Name
                  </th>
                  <th
                    class="px-2 py-2 text-left font-medium text-gray-700 dark:text-gray-300 text-xs"
                  >
                    Email
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  *ngFor="let invitation of csvData; let i = index"
                  class="border-b dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <td
                    class="px-2 py-2 text-gray-600 dark:text-gray-400 text-xs"
                  >
                    {{ i + 1 }}
                  </td>
                  <td
                    class="px-2 py-2 font-medium text-gray-900 dark:text-white text-xs"
                  >
                    {{ invitation.name }}
                  </td>
                  <td
                    class="px-2 py-2 text-gray-700 dark:text-gray-300 text-xs"
                  >
                    {{ invitation.email }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div
            class="text-xs text-gray-500 dark:text-gray-400 bg-blue-50 dark:bg-blue-900/20 p-2 rounded"
          >
            <div class="flex items-center">
              <svg
                class="w-4 h-4 mr-2 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              All {{ csvData.length }} invitations have been validated and are
              ready to send. Review the data above before proceeding.
            </div>
          </div>
        </div>

        <div
          class="flex items-center p-4 md:p-5 border-t border-gray-200 rounded-b dark:border-gray-600"
        >
          <!-- Import Button -->
          <button
            *ngIf="csvData.length > 0 && csvErrors.length === 0"
            (click)="importInvitations()"
            [disabled]="isProcessingCsv"
            type="button"
            class="text-white bg-green-700 hover:bg-green-800 focus:ring-4 focus:outline-none focus:ring-green-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-green-600 dark:hover:bg-green-700 dark:focus:ring-green-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span
              *ngIf="!isProcessingCsv"
              class="flex items-center justify-center"
            >
              <svg
                class="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              Send {{ csvData.length }} Invitations
            </span>
            <span
              *ngIf="isProcessingCsv"
              class="flex items-center justify-center"
            >
              <svg
                class="animate-spin h-4 w-4 mr-2"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Sending Invitations...
            </span>
          </button>
          <button
            (click)="closeModals()"
            type="button"
            class="ms-3 text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-blue-300 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-500 dark:hover:text-white dark:hover:bg-gray-600 dark:focus:ring-gray-600"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Send Invitation Modal -->
  <div
    *ngIf="isSendInvitationModalOpen"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
  >
    <div
      class="bg-white rounded-lg shadow dark:bg-gray-700 w-full max-w-md mx-4"
    >
      <div
        class="flex items-center justify-between p-4 md:p-5 border-b rounded-t dark:border-gray-600"
      >
        <h3
          class="text-lg font-semibold text-gray-900 dark:text-white flex items-center"
        >
          <svg
            class="w-6 h-6 mr-2 text-blue-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
          Send Invitation
        </h3>
        <button
          (click)="closeModals()"
          type="button"
          class="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white"
        >
          <svg class="w-3 h-3" fill="none" viewBox="0 0 14 14">
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
            />
          </svg>
        </button>
      </div>

      <div class="p-4 md:p-5">
        <form class="space-y-4">
          <div
            class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4"
          >
            <div class="flex items-start space-x-3">
              <svg
                class="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div class="flex-1">
                <h4
                  class="text-sm font-medium text-blue-900 dark:text-blue-100"
                >
                  Invitation Process
                </h4>
                <p class="text-sm text-blue-700 dark:text-blue-300 mt-1">
                  An invitation email with a unique code will be sent to the
                  provided email address. The user will be able to use the code
                  to complete their registration.
                </p>
              </div>
            </div>
          </div>

          <div>
            <label
              class="block mb-2 text-sm font-medium text-gray-900 dark:text-white flex items-center"
            >
              <svg
                class="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              Name *
            </label>
            <input
              type="text"
              [(ngModel)]="newInvitation.name"
              name="name"
              [class]="
                newInvitationErrors.name ?
                  'bg-red-50 border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500'
                : 'bg-gray-50 border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'
              "
              class="text-sm rounded-lg block w-full p-2.5 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400 dark:text-white"
              placeholder="Enter full name"
              required
            />
            <p
              *ngIf="newInvitationErrors.name"
              class="mt-1 text-xs text-red-600 flex items-center"
            >
              <svg
                class="w-3 h-3 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {{ newInvitationErrors.name }}
            </p>
          </div>

          <div>
            <label
              class="block mb-2 text-sm font-medium text-gray-900 dark:text-white flex items-center"
            >
              <svg
                class="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              Email *
            </label>
            <input
              type="email"
              [(ngModel)]="newInvitation.email"
              name="email"
              [class]="
                newInvitationErrors.email ?
                  'bg-red-50 border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500'
                : 'bg-gray-50 border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'
              "
              class="text-sm rounded-lg block w-full p-2.5 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400 dark:text-white"
              placeholder="Enter email address"
              required
            />
            <p
              *ngIf="newInvitationErrors.email"
              class="mt-1 text-xs text-red-600 flex items-center"
            >
              <svg
                class="w-3 h-3 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {{ newInvitationErrors.email }}
            </p>
          </div>

          <!-- User Type Dropdown - Only visible for internal users -->
          <div *ngIf="isInternalUser">
            <label
              class="block mb-2 text-sm font-medium text-gray-900 dark:text-white flex items-center"
            >
              <svg
                class="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              User Type *
            </label>
            <select
              [(ngModel)]="newInvitation.userType"
              name="userType"
              class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400 dark:text-white"
            >
              <option value="GROWER">Grower</option>
              <option value="MANAGER">Manager</option>
            </select>
          </div>

          <!-- Client Dropdown - Required for internal users (both Manager and Grower) -->
          <div *ngIf="isInternalUser">
            <label
              class="block mb-2 text-sm font-medium text-gray-900 dark:text-white flex items-center"
            >
              <svg
                class="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
              Client *
            </label>
            <select
              [(ngModel)]="newInvitation.clientId"
              name="clientId"
              [class]="
                newInvitationErrors.clientId ?
                  'bg-red-50 border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500'
                : 'bg-gray-50 border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'
              "
              class="text-sm rounded-lg block w-full p-2.5 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400 dark:text-white"
            >
              <option [ngValue]="null" disabled>Select a client</option>
              <option *ngFor="let client of clients" [ngValue]="client.id">
                {{ client.name }}
              </option>
            </select>
            <p
              *ngIf="newInvitationErrors.clientId"
              class="mt-1 text-xs text-red-600 flex items-center"
            >
              <svg
                class="w-3 h-3 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {{ newInvitationErrors.clientId }}
            </p>
          </div>
        </form>
      </div>

      <div
        class="flex items-center p-4 md:p-5 border-t border-gray-200 rounded-b dark:border-gray-600"
      >
        <button
          (click)="sendUserInvitation()"
          type="button"
          class="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800 flex items-center"
        >
          <svg
            class="w-4 h-4 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
          Send Invitation
        </button>
        <button
          (click)="closeModals()"
          type="button"
          class="ms-3 text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-blue-300 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-500 dark:hover:text-white dark:hover:bg-gray-600 dark:focus:ring-gray-600"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>

  <!-- Edit User Modal (Simplified) -->
  <div
    *ngIf="isEditModalOpen"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
  >
    <div
      class="bg-white rounded-lg shadow dark:bg-gray-700 w-full max-w-md mx-4"
    >
      <div
        class="flex items-center justify-between p-4 md:p-5 border-b rounded-t dark:border-gray-600"
      >
        <h3
          class="text-lg font-semibold text-gray-900 dark:text-white flex items-center"
        >
          <svg
            class="w-6 h-6 mr-2 text-orange-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
          Edit User
        </h3>
        <button
          (click)="closeModals()"
          type="button"
          class="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white"
        >
          <svg class="w-3 h-3" fill="none" viewBox="0 0 14 14">
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
            />
          </svg>
        </button>
      </div>

      <div class="p-4 md:p-5">
        <form class="space-y-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label
                class="block mb-2 text-sm font-medium text-gray-900 dark:text-white flex items-center"
              >
                <svg
                  class="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
                First Name *
              </label>
              <input
                type="text"
                [(ngModel)]="editUser.first_name"
                name="first_name"
                [class]="
                  editUserErrors.first_name ?
                    'bg-red-50 border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500'
                  : 'bg-gray-50 border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'
                "
                class="text-sm rounded-lg block w-full p-2.5 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400 dark:text-white"
                required
              />
              <p
                *ngIf="editUserErrors.first_name"
                class="mt-1 text-xs text-red-600 flex items-center"
              >
                <svg
                  class="w-3 h-3 mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                {{ editUserErrors.first_name }}
              </p>
            </div>

            <div>
              <label
                class="block mb-2 text-sm font-medium text-gray-900 dark:text-white flex items-center"
              >
                <svg
                  class="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
                Last Name *
              </label>
              <input
                type="text"
                [(ngModel)]="editUser.last_name"
                name="last_name"
                [class]="
                  editUserErrors.last_name ?
                    'bg-red-50 border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500'
                  : 'bg-gray-50 border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'
                "
                class="text-sm rounded-lg block w-full p-2.5 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400 dark:text-white"
                required
              />
              <p
                *ngIf="editUserErrors.last_name"
                class="mt-1 text-xs text-red-600 flex items-center"
              >
                <svg
                  class="w-3 h-3 mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                {{ editUserErrors.last_name }}
              </p>
            </div>
          </div>

          <div>
            <label
              class="block mb-2 text-sm font-medium text-gray-900 dark:text-white flex items-center"
            >
              <svg
                class="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              Email *
            </label>
            <input
              type="email"
              [(ngModel)]="editUser.email"
              name="email"
              [class]="
                editUserErrors.email ?
                  'bg-red-50 border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500'
                : 'bg-gray-50 border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'
              "
              class="text-sm rounded-lg block w-full p-2.5 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400 dark:text-white"
              required
            />
            <p
              *ngIf="editUserErrors.email"
              class="mt-1 text-xs text-red-600 flex items-center"
            >
              <svg
                class="w-3 h-3 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {{ editUserErrors.email }}
            </p>
          </div>

          <div>
            <label
              class="block mb-2 text-sm font-medium text-gray-900 dark:text-white flex items-center"
            >
              <svg
                class="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
              Phone
            </label>
            <input
              type="tel"
              [(ngModel)]="editUser.phone"
              name="phone"
              [class]="
                editUserErrors.phone ?
                  'bg-red-50 border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500'
                : 'bg-gray-50 border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'
              "
              class="text-sm rounded-lg block w-full p-2.5 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400 dark:text-white"
            />
            <p
              *ngIf="editUserErrors.phone"
              class="mt-1 text-xs text-red-600 flex items-center"
            >
              <svg
                class="w-3 h-3 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {{ editUserErrors.phone }}
            </p>
          </div>
        </form>
      </div>

      <div
        class="flex items-center p-4 md:p-5 border-t border-gray-200 rounded-b dark:border-gray-600"
      >
        <button
          (click)="updateUser()"
          type="button"
          class="text-white bg-orange-700 hover:bg-orange-800 focus:ring-4 focus:outline-none focus:ring-orange-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-orange-600 dark:hover:bg-orange-700 dark:focus:ring-orange-800 flex items-center"
        >
          <svg
            class="w-4 h-4 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
          Update User
        </button>
        <button
          (click)="closeModals()"
          type="button"
          class="ms-3 text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-blue-300 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-500 dark:hover:text-white dark:hover:bg-gray-600 dark:focus:ring-gray-600"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>

  <!-- Delete Invitation Modal -->
  <div
    *ngIf="isDeleteInvitationModalOpen"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
  >
    <div
      class="bg-white rounded-lg shadow dark:bg-gray-700 w-full max-w-md mx-4"
    >
      <div class="p-6 text-center">
        <svg
          class="mx-auto mb-4 text-red-400 w-12 h-12 dark:text-red-300"
          fill="none"
          viewBox="0 0 20 20"
        >
          <path
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M10 11V6m0 8h.01M19 10a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
          />
        </svg>
        <h3 class="mb-5 text-lg font-normal text-gray-500 dark:text-gray-100">
          Delete invitation for
          <strong class="text-red-600">{{ selectedInvitation?.email }}</strong
          >?
        </h3>
        <p class="text-sm text-gray-400 dark:text-gray-100 mb-6">
          This will revoke the invitation link/code for this email.
        </p>
        <div class="flex justify-center space-x-3">
          <button
            (click)="confirmDeleteInvitation()"
            type="button"
            class="text-white bg-red-600 hover:bg-red-800 focus:ring-4 focus:outline-none focus:ring-red-300 dark:focus:ring-red-800 font-medium rounded-lg text-sm inline-flex items-center px-5 py-2.5 text-center"
          >
            <svg
              class="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
            Yes, delete
          </button>
          <button
            (click)="cancelDeleteInvitation()"
            type="button"
            class="text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-gray-200 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-500 dark:hover:text-white dark:hover:bg-gray-600 dark:focus:ring-gray-600"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Delete User Modal -->
  <div
    *ngIf="isDeleteModalOpen"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
  >
    <div
      class="bg-white rounded-lg shadow dark:bg-gray-700 w-full max-w-md mx-4"
    >
      <div class="p-6 text-center">
        <svg
          class="mx-auto mb-4 text-red-400 w-12 h-12 dark:text-red-300"
          fill="none"
          viewBox="0 0 20 20"
        >
          <path
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M10 11V6m0 8h.01M19 10a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
          />
        </svg>
        <h3 class="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">
          Are you sure you want to delete user
          <strong class="text-red-600">{{
            getUserDisplayName(selectedUser)
          }}</strong
          >?
        </h3>
        <p class="text-sm text-gray-400 dark:text-gray-500 mb-6">
          This action cannot be undone. All user data will be permanently
          removed.
        </p>
        <div class="flex justify-center space-x-3">
          <!-- Delete temporarily disabled
          <button
            (click)="deleteUser()"
            type="button"
            class="text-white bg-red-600 hover:bg-red-800 focus:ring-4 focus:outline-none focus:ring-red-300 dark:focus:ring-red-800 font-medium rounded-lg text-sm inline-flex items-center px-5 py-2.5 text-center"
          >
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
            Yes, delete
          </button>
          -->
          <button
            (click)="closeModals()"
            type="button"
            class="text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-gray-200 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-500 dark:hover:text-white dark:hover:bg-gray-600 dark:focus:ring-gray-600"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Bulk Invitation Result Modal -->
  <div
    *ngIf="showBulkInvitationResult && bulkInvitationResult"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
  >
    <div
      class="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-md w-full mx-4 p-6"
    >
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
          {{ bulkResultTitle }}
        </h3>
        <button
          type="button"
          (click)="closeBulkInvitationResult()"
          class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          aria-label="Close"
        >
          <svg
            class="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
      <div class="space-y-2 text-sm text-gray-700 dark:text-gray-200">
        <div
          class="flex items-center gap-2"
          *ngIf="bulkInvitationResult.newInvitationsCount > 0"
        >
          <span class="text-green-600">✓</span>
          <span
            >{{ bulkInvitationResult.newInvitationsCount }} New
            {{ bulkInvitationResult.invitedRoleLabel
            }}{{
              bulkInvitationResult.newInvitationsCount === 1 ? "" : "s"
            }}
            Invited</span
          >
        </div>
        <div
          class="flex items-center gap-2"
          *ngIf="bulkInvitationResult.districtAccessAddedCount > 0"
        >
          <span class="text-green-600">✓</span>
          <span
            >{{ bulkInvitationResult.districtAccessAddedCount }} Existing
            Grower{{
              bulkInvitationResult.districtAccessAddedCount === 1 ? "" : "s"
            }}
            Added to This District</span
          >
        </div>
        <div *ngIf="bulkInvitationResult.skipped.length > 0" class="pt-2">
          <div
            class="flex items-center gap-2 text-yellow-700 dark:text-yellow-500 mb-1"
          >
            <span>⚠</span>
            <span>{{ bulkInvitationResult.skipped.length }} Skipped</span>
          </div>
          <ul
            class="ml-6 list-disc text-xs space-y-1 text-gray-600 dark:text-gray-300"
          >
            <li *ngFor="let item of bulkInvitationResult.skipped">
              {{ item.email }} — {{ item.reason }}
            </li>
          </ul>
        </div>
        <div
          *ngIf="bulkInvitationResult.failuresCount > 0"
          class="pt-2 text-red-600 dark:text-red-400"
        >
          <div class="flex items-center gap-2">
            <span>✗</span>
            <span
              >{{ bulkInvitationResult.failuresCount }} Failed (see errors
              below)</span
            >
          </div>
        </div>
        <div
          *ngIf="
            bulkInvitationResult.newInvitationsCount === 0 &&
            bulkInvitationResult.districtAccessAddedCount === 0 &&
            bulkInvitationResult.skipped.length === 0 &&
            bulkInvitationResult.failuresCount === 0
          "
          class="text-gray-500"
        >
          No invitations were processed.
        </div>
      </div>
      <div class="flex justify-end mt-6">
        <button
          type="button"
          (click)="closeBulkInvitationResult()"
          class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 dark:bg-blue-500 dark:hover:bg-blue-600"
        >
          OK
        </button>
      </div>
    </div>
  </div>
</div>
